/**
 * Pure helpers for the Media Centre component.
 *
 * These are all side-effect-free (no component state) and were extracted from
 * MediaCentre.js to slim the component down and make the logic reusable/testable.
 * Behaviour is intentionally identical to the original inline versions.
 */

export const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** Convert a YouTube/Vimeo watch URL to an embeddable URL (used by the video element/iframe). */
export const getEmbeddableVideoUrl = (url) => {
    if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('v=')[1].split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com/')) {
        const videoId = url.split('vimeo.com/')[1].split('?')[0];
        return `https://player.vimeo.com/video/${videoId}`;
    }
    return url; // Return original URL if not YouTube/Vimeo
};

/** Build a Vimeo player embed URL, preserving the privacy hash for private videos. */
export const getVimeoEmbedUrl = (vimeoUrl) => {
    // Match both regular and private video URLs with privacy hash
    const match = vimeoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
    if (!match) return null;

    const videoId = match[1];
    const privacyHash = match[2]; // Will be undefined for public videos

    let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;

    if (privacyHash) {
        embedUrl += `&h=${privacyHash}`;
    }

    return embedUrl;
};

/** Resolve a YouTube thumbnail URL from a watch URL. Returns null if not parseable. */
export const getYouTubeThumbnail = (youtubeUrl) => {
    try {
        const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
        if (!videoId) {
            return null;
        }
        // Use maxresdefault for best quality, fallback to hqdefault
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    } catch (err) {
        console.error('Error fetching YouTube thumbnail:', err);
        return null;
    }
};

/** Resolve a Vimeo thumbnail URL via oEmbed, falling back to the CDN pattern. Async. */
export const getVimeoThumbnail = async (vimeoUrl) => {
    try {
        const videoId = vimeoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
        if (!videoId) {
            return null;
        }

        // Method 1: Try oEmbed API (modern approach)
        try {
            const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}&width=640`;
            const oembedResponse = await fetch(oembedUrl);

            if (!oembedResponse.ok) {
                if (oembedResponse.status === 404) {
                    console.warn(`Vimeo video ${videoId} not found via oEmbed. Video may have been deleted or made private.`);
                }
                throw new Error(`oEmbed API returned ${oembedResponse.status}`);
            }

            const oembedData = await oembedResponse.json();

            if (oembedData.thumbnail_url) {
                return oembedData.thumbnail_url;
            }
        } catch (oembedError) {
            // oEmbed failed, continue to CDN fallback
            console.warn(`oEmbed failed for video ${videoId}:`, oembedError.message);
        }

        // Method 2: Try direct CDN approach
        try {
            const cdnUrl = `https://i.vimeocdn.com/video/${videoId}_640x360.jpg`;
            // Test if the URL actually works
            const testResponse = await fetch(cdnUrl, { method: 'HEAD' });
            if (testResponse.ok) {
                return cdnUrl;
            }
        } catch (cdnError) {
            // CDN approach failed
        }

        return null;
    } catch (err) {
        console.error('Error fetching Vimeo thumbnail:', err);
        return null;
    }
};

/** Format a Date as DD/MM/YYYY (empty string for falsy input). */
export const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/** Map a theme name to its colour class. */
export const getThemeColor = (theme) => {
    const colorMap = {
        'Tax': 'cornflower',
        'ESG': 'mint',
        'Financial Literacy': 'gold',
        'Digitalisation': 'coral',
        'Product': 'dust-rose',
        'Legal Structures': 'cyan',
        'Innovation': 'peach',
        'Strategy': 'sage',
    };
    return colorMap[theme] || 'light-gray';
};

/** Map a product/service name to its colour class. */
export const getProductColor = (product) => {
    const colorMap = {
        'ETFs': 'cornflower',
        'Hedge Fund Strategies': 'mint',
        'Traditional Fund Strategies': 'gold',
        'Digital Assets': 'coral',
        'Private Asset Strategies': 'sage',
        'Management Companies': 'dark-red',
        'Industry Competitiveness': 'blue',
    };
    return colorMap[product] || 'light-gray';
};

/** FontAwesome icon class for a media type (case-insensitive). */
export const getMediaTypeIcon = (mediaType) => {
    switch ((mediaType || '').toLowerCase()) {
        case 'video': return 'fas fa-play';
        case 'audio': return 'fas fa-headphones';
        case 'image': return 'fas fa-image';
        case 'briefing': return 'fas fa-headphones';
        default: return 'fas fa-file';
    }
};

/** Human-readable label for a media type. */
export const getMediaTypeDisplay = (mediaType) => {
    switch (mediaType.toLowerCase()) {
        case 'video': return 'Video';
        case 'audio': return 'Audio';
        case 'image': return 'Image';
        case 'briefing': return 'Briefing';
        default: return 'Media';
    }
};

/**
 * A Briefing plays through the embedded Wolfox widget rather than a media file,
 * so it is identified by its briefing code instead of a webURLLink.
 */
export const isBriefing = (mediaType) => (mediaType || '').toLowerCase() === 'briefing';

/** Whether a media type should show a duration badge. */
export const showDuration = (mediaType) =>
    mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio' || mediaType === 'Audio' || mediaType === 'Video';

/** Whether a media type should show a play overlay. */
export const showPlayOverlay = (mediaType) =>
    mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio' || mediaType === 'Audio' || mediaType === 'Video' ||
    isBriefing(mediaType);
